// Assets
import logo from "@/assets/logos/planetrus-horizontal-logo.png";

// Components
import LocaleDropdown from "../dropdowns/LocaleDropdown";

// Hooks
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

// Icons
import { RxHamburgerMenu } from "react-icons/rx";
import { IoMdClose } from "react-icons/io";

// Utils
import { twMerge } from "tailwind-merge";
import { convertUnderscoresToHyphens, getFirstPathSegment } from "@/utils/functions";
import SearchInput from "../inputs/SearchInput";

interface DropdownProps {
  parentRect: DOMRect | null;
  children: React.ReactNode;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const firstPathSegment = getFirstPathSegment(location.pathname);

  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = (index: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveDropdown(index);
  };

  const handleMouseLeave = () => {
    // Add a small delay before closing the dropdown
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 100);
  };

  // Internal Dropdown component using Portal
  const Dropdown = ({ parentRect, children, onMouseEnter, onMouseLeave }: DropdownProps) => {
    if (!mounted || !parentRect) return null;

    const isRtl = i18n.language === "ar";
    return createPortal(
      <div
        className="fixed z-50 min-w-fit rounded-md bg-[#e8f0ec] p-3 py-2 shadow-lg"
        style={{
          top: `${parentRect.bottom + 4}px`,
          ...(isRtl ? { right: `${window.innerWidth - parentRect.right}px` } : { left: `${parentRect.left}px` }),
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {children}
      </div>,
      document.body
    );
  };

  const data = [
    {
      title: "home",
      subTitle: null,
      path: "",
    },
    {
      title: "about_us",
      subTitle: ["about_planetrus", "meet_our_team"],
      path: "about-us",
    },
    {
      title: "why_us",
      subTitle: null,
      path: "why-us",
    },
    {
      title: "our_commitments",
      subTitle: null,
      path: "our-commitments",
    },
    {
      title: "sustainability",
      subTitle: ["used_cooking_oil_recycling", "the_circular_economy"],
      path: "sustainability",
    },
    {
      title: "blog",
      subTitle: null,
      path: "blog",
    },
    {
      title: "contact_us",
      subTitle: null,
      path: "contact-us",
    },
  ];

  // handle title navigation
  const handleTitleNavigation = (path: string, subTitle: string[] | null) => {
    if (subTitle) return;
    navigate(`/${path}`);
  };

  // Add this function to handle mobile menu toggle
  const toggleMobileMenu = () => {
    const newMenuState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newMenuState);
    
    // If opening the menu, scroll to top smoothly
    if (newMenuState) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("body-lock");
    } else {
      document.body.classList.remove("body-lock");
    }

    return () => {
      document.body.classList.remove("body-lock");
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="sticky top-0 z-50 flex h-fit min-h-[80px] w-full flex-col bg-white pt-4 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)] md:min-h-[132px]">
      <main className="mx-auto h-full w-full max-w-[1440px] px-4 md:px-8 lg:px-[120px]">
        <section className="flex h-full w-full justify-between">
          <div className="flex h-[80px] w-[180px] items-center md:h-auto md:w-[244px]">
            <figure className="cursor-pointer overflow-hidden" onClick={() => navigate("/")}>
              <img src={logo} alt="horizontal-logo" className="w-full" />
            </figure>
          </div>

          <div className="flex items-center lg:hidden">
            <button onClick={toggleMobileMenu} className="p-2 text-primary" aria-label="Toggle menu">
              {isMobileMenuOpen ? <IoMdClose size={24} /> : <RxHamburgerMenu size={24} />}
            </button>
          </div>

          <div className="hidden w-full max-w-[950px] lg:block">
            <section className="flex w-full items-center justify-end">
              <div className="flex items-center gap-6">
                <SearchInput />
                <LocaleDropdown />
              </div>
            </section>
            <section className="mt-3 flex w-full flex-wrap items-center gap-4" style={{ paddingInlineStart: "2.5rem" }}>
              {data.map((item, index) => (
                <div
                  key={index}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className="relative"
                  onMouseEnter={() => item.subTitle && handleMouseEnter(index)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div
                    className={twMerge(
                      `w-fit cursor-pointer whitespace-nowrap rounded-md px-6 py-3 text-sm font-semibold leading-normal
                      ${firstPathSegment === item.path ? "bg-primary text-white" : "hover-bg-primary text-primary"}
                      `
                    )}
                    onClick={() => handleTitleNavigation(item.path, item.subTitle)}
                  >
                    {t(`navbar.${item.title}`)}
                  </div>
                  {item.subTitle && activeDropdown === index && (
                    <Dropdown
                      parentRect={itemRefs.current[index]?.getBoundingClientRect() ?? null}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {item.subTitle.map((subItem, subIndex) => (
                        <div
                          key={subIndex}
                          className="cursor-pointer rounded-[3px] px-3 py-1.5 text-sm font-bold leading-normal text-primary hover:bg-[#CCCDD54D]"
                          onClick={() => {
                            navigate(`/${item.path}/${convertUnderscoresToHyphens(subItem)}`);
                            setActiveDropdown(null);
                          }}
                        >
                          {t(`navbar.${subItem}`)}
                        </div>
                      ))}
                    </Dropdown>
                  )}
                </div>
              ))}
            </section>
          </div>
        </section>
      </main>

      {isMobileMenuOpen && (
        <div className="w-full bg-white lg:hidden">
          <div className="mx-auto w-full max-w-[1440px]">
            <div className="flex flex-col space-y-2 p-4">
              <div className="flex flex-col gap-4 border-b border-gray-200 pb-4">
                <SearchInput />
                <LocaleDropdown />
              </div>

              {data.map((item, index) => (
                <div key={index} className="flex flex-col">
                  <div
                    className={twMerge(
                      `w-full cursor-pointer rounded-md px-4 py-3 text-sm font-semibold
                      ${firstPathSegment === item.path ? "bg-primary text-white" : "text-primary"}
                      `
                    )}
                    onClick={() => {
                      handleTitleNavigation(item.path, item.subTitle);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {t(`navbar.${item.title}`)}
                  </div>
                  {item.subTitle && (
                    <div className="mt-1 flex flex-col space-y-1" style={{ marginInlineStart: "1rem" }}>
                      {item.subTitle.map((subItem, subIndex) => (
                        <div
                          key={subIndex}
                          className="cursor-pointer rounded-[3px] px-3 py-1.5 text-sm font-bold text-primary hover:bg-[#CCCDD54D]"
                          onClick={() => {
                            navigate(`/${item.path}/${convertUnderscoresToHyphens(subItem)}`);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          {t(`navbar.${subItem}`)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
